import { BusinessInformationOccupantListItem, UserPermissions } from '@centecomm/portal-service';
import { useTenantBusiness, useCurrentUser } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../../Types';
import { BusinessActions, BusinessActionTypes } from '../actions';
import { mapBusinessAPIOccupantToOccupant } from '../Mappers/mapBusinessAPIOccupantToOccupant';
import * as selectors from '../selectors';
import { OccupantWithPermissionFlag, Occupant } from '../Types';

type BusinessOccupantsFromStateHook = (permissions: UserPermissions[]) => {
    areLoaded: boolean;
    areLoading: boolean;
    hasError: boolean;
    loadState: LoadStatus;
    occupants: Occupant[];
    permissionsCheckedOccupants: OccupantWithPermissionFlag[];
};

const useBusinessOccupantsFromState: BusinessOccupantsFromStateHook = (permissions = []) => {
    const dispatch = useDispatch();
    const { hasOccupantPermission } = useCurrentUser();

    const loadState: LoadStatus = useSelector(selectors.occupantsLoadStatus);
    const occupants: Occupant[] = useSelector(selectors.sortedOccupantsList);

    const { getOccupantBusinessDetailList } = useTenantBusiness();

    React.useEffect(() => {
        if (loadState === LoadStatus.INITIAL_STATE) {
            dispatch({
                type: BusinessActions.LOAD_BUSINESS_OCCUPANTS,
            });

            getOccupantBusinessDetailList()
                .then((fetchedOccupants) => {
                    dispatch({
                        type: BusinessActions.SET_BUSINESS_OCCUPANTS,
                        payload: fetchedOccupants.map((o: BusinessInformationOccupantListItem) =>
                            mapBusinessAPIOccupantToOccupant(o),
                        ),
                    } as BusinessActionTypes);
                })
                .catch((error) => {
                    dispatch({
                        type: BusinessActions.SET_BUSINESS_OCCUPANTS_ERROR_STATE,
                        payload: {
                            errorMessage: error?.code
                                ? error.code
                                : 'We were not able to load your business information at this time.',
                        },
                    });
                });
        }
    }, [loadState]);

    // Places flag on occupant object to show if it has the given permissions. The
    // current use case of this is to show a list with active links for those occupants
    // that pass the ViewOccupant permission and static display for those that don't
    const permissionsCheckedOccupants: OccupantWithPermissionFlag[] = React.useMemo(
        () =>
            occupants.map((occupant) => ({
                ...occupant,
                hasPermission: permissions.some((permission) =>
                    hasOccupantPermission(permission, occupant.propertyId, occupant.id),
                ),
            })),
        [occupants, permissions, hasOccupantPermission],
    );

    return {
        areLoaded: loadState === LoadStatus.LOADED,
        areLoading: loadState === LoadStatus.PENDING,
        hasError: loadState === LoadStatus.ERROR,
        loadState,
        occupants,
        permissionsCheckedOccupants,
    };
};

export default useBusinessOccupantsFromState;
